import React, {
  Key,
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from "react";
import { Table as TableIcon, ChevronDown, ChevronUp } from "lucide-react";
import AutoSizer from "react-virtualized-auto-sizer";
import {
  VariableSizeList as List,
  ListChildComponentProps,
} from "react-window";
import { useCurrency } from "@/context/CurrencyContext";
import { useTranslation } from "react-i18next";
import { Body0, Body2, H4 } from "../ui/Typography";
import LoadingPage from "../LoadingPage";

type Direction = "asc" | "desc";

export type RawDataTableProps = {
  data: any;
  showTitle?: boolean;
  rowHeight?: number;
  heightClassName?: string;
  minLoadingMs?: number;
};

const NUMERIC_COLS = new Set(["debit", "credit"]);
const isNumeric = (v: unknown) =>
  (typeof v === "number" && !isNaN(v)) ||
  (typeof v === "string" && v.trim() !== "" && !isNaN(Number(v)));

function useStickyPending(pending: boolean, minMs = 250) {
  const [visible, setVisible] = useState(false);
  const startedAt = useRef<number | null>(null);

  useEffect(() => {
    if (pending) {
      if (!visible) setVisible(true);
      if (startedAt.current == null) startedAt.current = performance.now();
      return;
    }
    const now = performance.now();
    const elapsed = startedAt.current ? now - startedAt.current : 0;
    const wait = Math.max(0, minMs - elapsed);
    const id = window.setTimeout(() => {
      startedAt.current = null;
      setVisible(false);
    }, wait);
    return () => clearTimeout(id);
  }, [pending, minMs]);

  return visible;
}

const RawDataTable: React.FC<RawDataTableProps> = ({
  data,
  showTitle = true,
  rowHeight = 44,
  heightClassName = "h-[70vh]",
  minLoadingMs = 250,
}) => {
  const [sortKey, setSortKey] = useState<string>("transactionDate");
  const [sortDirection, setSortDirection] = useState<Direction>("desc");
  const { formatNumber, unit } = useCurrency();
  const { t } = useTranslation("common");
  const [isSortingPending, startTransition] = useTransition();

  const prevUnitRef = useRef(unit);
  const [currencyPending, setCurrencyPending] = useState(false);
  useEffect(() => {
    if (prevUnitRef.current !== unit) {
      prevUnitRef.current = unit;
      setCurrencyPending(true);
      const id = setTimeout(() => setCurrencyPending(false), 0);
      return () => clearTimeout(id);
    }
  }, [unit]);

  const isLoadingSticky = useStickyPending(
    isSortingPending || currencyPending,
    minLoadingMs
  );

  const [scrollbarWidth, setScrollbarWidth] = useState(0);
  useLayoutEffect(() => {
    const el = document.createElement("div");
    el.style.cssText =
      "position:absolute;top:-9999px;width:100px;height:100px;overflow:scroll;";
    document.body.appendChild(el);
    setScrollbarWidth(el.offsetWidth - el.clientWidth);
    document.body.removeChild(el);
  }, []);

  const hasData = Array.isArray(data) && data.length > 0;

  const headers = useMemo(
    () => (hasData ? Object.keys(data[0] ?? {}) : []),
    [hasData, data]
  );

  const gridTemplate = useMemo(() => {
    return headers
      .map((h) => {
        const lower = h.toLowerCase();
        if (
          lower === "accountnumber" ||
          lower === "credit" ||
          lower === "debit" ||
          lower === "project"
        )
          return "minmax(100px, 0.7fr)";
        return "minmax(150px, 1fr)";
      })
      .join(" ");
  }, [headers]);

  const handleSort = (key: string) => {
    startTransition(() => {
      if (key === sortKey)
        setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
      else {
        setSortKey(key);
        setSortDirection("asc");
      }
    });
  };

  const sortedData = useMemo(() => {
    if (!hasData) return [];
    const arr = [...data];
    arr.sort((a, b) => {
      const valA = a[sortKey];
      const valB = b[sortKey];

      if (valA === undefined || valB === undefined) return 0;

      if (isNumeric(valA) && isNumeric(valB)) {
        return sortDirection === "asc"
          ? Number(valA) - Number(valB)
          : Number(valB) - Number(valA);
      }

      const dateA = new Date(valA as any);
      const dateB = new Date(valB as any);
      if (!isNaN(dateA.getTime()) && !isNaN(dateB.getTime())) {
        return sortDirection === "asc"
          ? dateA.getTime() - dateB.getTime()
          : dateB.getTime() - dateA.getTime();
      }

      return sortDirection === "asc"
        ? String(valA).localeCompare(String(valB))
        : String(valB).localeCompare(String(valA));
    });
    return arr;
  }, [hasData, data, sortKey, sortDirection]);

  const listRef = useRef<List>(null);
  const sizeMapRef = useRef<Map<number, number>>(new Map());

  const getRowSize = useCallback(
    (index: number) => sizeMapRef.current.get(index) ?? rowHeight,
    [rowHeight]
  );

  const setRowSize = useCallback((index: number, size: number) => {
    const prev = sizeMapRef.current.get(index);
    if (prev !== size) {
      sizeMapRef.current.set(index, size);
      listRef.current?.resetAfterIndex(index, false);
    }
  }, []);

  useEffect(() => {
    sizeMapRef.current.clear();
    listRef.current?.resetAfterIndex(0, false);
  }, [gridTemplate, sortedData.length]);

  const Row = memo(({ index, style }: ListChildComponentProps) => {
    const containerRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
      if (!containerRef.current) return;

      const measure = () => {
        const cur = containerRef.current;
        if (!cur) return;
        const h = Math.ceil(cur.scrollHeight) + 1;
        setRowSize(index, Math.max(h, rowHeight));
      };

      measure();

      const ro = new ResizeObserver((entries) => {
        const target = entries[0]?.target as HTMLElement | undefined;
        const h = Math.ceil(target?.scrollHeight ?? rowHeight) + 1;
        setRowSize(index, Math.max(h, rowHeight));
      });

      ro.observe(containerRef.current);

      return () => ro.disconnect();
    }, [index, rowHeight, setRowSize]);

    const entry = sortedData[index];

    return (
      <div
        style={style}
        className="bg-white"
        role="row"
        aria-rowindex={index + 2}
      >
        <div
          ref={containerRef}
          className="grid items-start"
          style={{ gridTemplateColumns: gridTemplate }}
        >
          {headers.map((key) => {
            const value = entry[key];
            const numeric = NUMERIC_COLS.has(key) && isNumeric(value);
            return (
              <Body2
                key={key}
                as="div"
                className="px-4 py-2 break-words whitespace-normal"
              >
                {numeric ? formatNumber(Number(value)) : String(value ?? "")}
              </Body2>
            );
          })}
        </div>
      </div>
    );
  });
  Row.displayName = "Row";

  const itemKey = useCallback(
    (index: number) =>
      (sortedData[index]?.id ??
        sortedData[index]?.verificationNumber ??
        `${index}-${sortedData[index]?.[sortKey]}`) as Key,
    [sortedData, sortKey]
  );

  if (isLoadingSticky) return <LoadingPage />;

  if (!hasData) {
    return (
      <div className="flex items-center justify-center h-40 text-gray-500 text-base font-medium">
        {t("no_data")}
      </div>
    );
  }

  return (
    <div className="overflow-hidden">
      {showTitle && (
        <div className="flex items-center space-x-2 mb-4">
          <TableIcon className="w-4 h-4" />
          <H4>{t("raw_data_header")}</H4>
        </div>
      )}

      <div className="border rounded-xl overflow-hidden">
        <div className="bg-[#f5f5f7]" style={{ paddingRight: scrollbarWidth }}>
          <div
            className="grid auto-rows-auto items-start"
            style={{ gridTemplateColumns: gridTemplate }}
          >
            {headers.map((header) => (
              <button
                key={header}
                onClick={() => handleSort(header)}
                className="px-4 py-2 capitalize cursor-pointer select-none flex items-start gap-1 text-left"
                role="columnheader"
                title={t(header)}
              >
                <Body0 className="truncate">{t(header)}</Body0>
                {sortKey === header &&
                  (sortDirection === "asc" ? (
                    <ChevronUp className="w-3 h-3 shrink-0 mt-0.5" />
                  ) : (
                    <ChevronDown className="w-3 h-3 shrink-0 mt-0.5" />
                  ))}
              </button>
            ))}
          </div>
        </div>

        <div
          className={`${heightClassName} overflow-auto [scrollbar-gutter:stable]`}
        >
          <AutoSizer>
            {({ height, width }) => (
              <List
                ref={listRef}
                height={height}
                width={width}
                itemCount={sortedData.length}
                itemSize={getRowSize}
                overscanCount={10}
                itemKey={itemKey}
                estimatedItemSize={rowHeight}
              >
                {Row}
              </List>
            )}
          </AutoSizer>
        </div>
      </div>
    </div>
  );
};

export default RawDataTable;
